// src/pages/league/LeagueDetailPage.tsx - Updated with real API
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  CogIcon,
  UsersIcon,
  PlayIcon,
  UserPlusIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
  ArrowsRightLeftIcon,
  InboxStackIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { StandingsResponse, Matchup, LeagueTransaction, Trade } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { useSeasonNow, datelineFor } from '../../hooks/useSeasonNow';
import StatusChip from '../../components/league/StatusChip';
import apiService from '../../services/api';
import { formatCurrency, formatDateTime } from '../../utils/typeUtils';
import { shouldShowMatchups, shouldShowStandings } from '../../utils/matchupVisibility';
import MatchupsCard from './MatchupsCard';
import BackLink from '../../components/common/BackLink';
import StartLeaguePanel from '../../components/league/StartLeaguePanel';
import LeagueWire from '../../components/league/LeagueWire';
import LineChart from '../../components/charts/LineChart';
import { cumulativeRace } from '../../utils/chartData';

export default function LeagueDetailPage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [startDraftLoading, setStartDraftLoading] = useState(false);
  const [startDraftError, setStartDraftError] = useState<string | null>(null);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [standings, setStandings] = useState<StandingsResponse | null>(null);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [standingsError, setStandingsError] = useState(false);

  const [matchups, setMatchups] = useState<Matchup[]>([]);
  const [matchupsLoading, setMatchupsLoading] = useState(false);
  const [matchupsError, setMatchupsError] = useState(false);
  const [scheduleGenerating, setScheduleGenerating] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [simulatingWeek, setSimulatingWeek] = useState(false);
  const [simulateError, setSimulateError] = useState<string | null>(null);

  const [transactions, setTransactions] = useState<LeagueTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [leagueTrades, setLeagueTrades] = useState<Trade[]>([]);

  const dateline = datelineFor(useSeasonNow());

  // Fetch league data from real API
  const { data: league, loading: leagueLoading, error: leagueError, refetch: refetchLeague } = useApi(
    () => leagueId ? apiService.getLeague(parseInt(leagueId)) : Promise.reject('No league ID'),
    [leagueId]
  );

  // Fetch teams data for this league
  const { data: teams, loading: teamsLoading, refetch: refetchTeams } = useApi(
    () => leagueId ? apiService.getTeams(parseInt(leagueId)) : Promise.resolve([]),
    [leagueId]
  );

  const handleJoinLeague = async () => {
    if (!leagueId || !teamName.trim()) return;

    setJoinLoading(true);
    try {
      console.log('Joining league with team name:', teamName);
      await apiService.joinLeague(parseInt(leagueId), teamName.trim());
      console.log('Successfully joined league');
      
      // Refresh both league and teams data
      await Promise.all([refetchLeague(), refetchTeams()]);
      setShowJoinModal(false);
      setTeamName('');
    } catch (error: any) {
      console.error('Failed to join league:', error);
      alert(error.message || 'Failed to join league. Please try again.');
    } finally {
      setJoinLoading(false);
    }
  };

  const handleStartDraft = async () => {
    if (!leagueId) return;
    setStartDraftLoading(true);
    setStartDraftError(null);
    try {
      await apiService.startDraft(parseInt(leagueId));
      navigate(`/app/leagues/${leagueId}/draft`);
    } catch (error: any) {
      setStartDraftError(error.message || 'Failed to start draft');
    } finally {
      setStartDraftLoading(false);
    }
  };

  const isCommissioner = league?.commissioner_id === user?.id;
  const userTeam = teams?.find(team => team.user_id === user?.id);
  const canStartDraft = isCommissioner && (teams?.length || 0) >= 2 && league?.league_status === 'setup';

  const showStandings = shouldShowStandings(league?.league_status);

  useEffect(() => {
    if (!leagueId || !showStandings) return;
    setStandingsLoading(true);
    setStandingsError(false);
    apiService.getStandings(parseInt(leagueId))
      .then(setStandings)
      .catch(() => setStandingsError(true))
      .finally(() => setStandingsLoading(false));
  }, [leagueId, showStandings]);

  const fetchMatchups = () => {
    if (!leagueId) return;
    setMatchupsLoading(true);
    setMatchupsError(false);
    apiService.getMatchups(parseInt(leagueId))
      .then(setMatchups)
      .catch(() => setMatchupsError(true))
      .finally(() => setMatchupsLoading(false));
  };

  useEffect(() => { fetchMatchups(); }, [leagueId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!leagueId || !showStandings) return;
    setTransactionsLoading(true);
    Promise.allSettled([
      apiService.getTransactions(parseInt(leagueId), 20),
      apiService.getLeagueTrades(parseInt(leagueId)),
    ]).then(([txns, trades]) => {
      if (txns.status === 'fulfilled') setTransactions(txns.value);
      if (trades.status === 'fulfilled') setLeagueTrades(trades.value);
    }).finally(() => setTransactionsLoading(false));
  }, [leagueId, showStandings]);

  const handleGenerateSchedule = async () => {
    if (!leagueId) return;
    setScheduleGenerating(true);
    setScheduleError(null);
    setScheduleMessage(null);
    try {
      const result = await apiService.generateSchedule(parseInt(leagueId));
      setScheduleMessage(result.message);
      fetchMatchups();
    } catch (err: any) {
      setScheduleError(err.message || 'Failed to generate schedule');
    } finally {
      setScheduleGenerating(false);
    }
  };

  const handleSimulateWeek = async (week: number) => {
    if (!leagueId) return;
    setSimulatingWeek(true);
    setSimulateError(null);
    try {
      await apiService.simulateWeek(parseInt(leagueId), week);
      // Refetch both matchups and standings — scores + W-L both change
      fetchMatchups();
      if (showStandings) {
        apiService.getStandings(parseInt(leagueId)).then(setStandings).catch(() => {});
      }
    } catch (err: any) {
      setSimulateError(err.message || 'Failed to simulate week');
    } finally {
      setSimulatingWeek(false);
    }
  };

  const handleRegenerateCode = async () => {
    if (!leagueId) return;
    if (!window.confirm('Regenerate invite code? The current invite link will stop working.')) return;
    setRegenerateLoading(true);
    try {
      await apiService.regenerateInviteCode(parseInt(leagueId));
      // Trigger a refetch so the new code propagates into the league object
      await refetchLeague();
    } catch (err: any) {
      alert(err.message || 'Failed to regenerate invite code');
    } finally {
      setRegenerateLoading(false);
    }
  };

  // Handle both new and legacy field formats
  const leagueType = league?.league_type || league?.leagueType || 'season_long';
  const maxTeams = league?.max_teams || league?.maxTeams || 10;
  const seasonYear = league?.season_year || league?.seasonYear || new Date().getFullYear();
  const entryFee = league?.entry_fee || league?.entryFee || 0;
  const isPublic = league?.is_public ?? league?.isPublic ?? false;
  const draftDate = league?.draft_date || league?.draftDate;
  const createdAt = league?.created_at || league?.createdAt || new Date().toISOString();

  if (leagueLoading) {
    return (
      <div className="space-y-6">
        <BackLink to="/app/leagues">Back to Leagues</BackLink>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-center py-12">
              <div className="loading-spinner w-8 h-8 mr-3"></div>
              <span>Loading league details...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (leagueError || !league) {
    return (
      <div className="space-y-6">
        <BackLink to="/app/leagues">Back to Leagues</BackLink>
        <div className="card">
          <div className="card-body text-center py-12">
            <h3 className="font-display uppercase text-lg tracking-wide text-ink-900 mb-2">
              {leagueError ? 'Error Loading League' : 'League Not Found'}
            </h3>
            <p className="text-secondary-600 mb-4">
              {leagueError || 'The league you\'re looking for doesn\'t exist.'}
            </p>
            <div className="flex justify-center">
              <Link to="/app/leagues" className="btn-primary">
                Back to Leagues
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        {/* Breadcrumb back link — own line, frees the title's full width */}
        <BackLink to="/app/leagues">Back to Leagues</BackLink>

        <div className="flex items-start justify-between gap-3 border-b-2 border-ink-900 pb-5">
          <div className="min-w-0">
            {/* The masthead its sibling pages already had. The league name is
                the most important title in the product and was set in the
                serif at 2xl, which broke the Scoreboard-and-Almanac rule and
                made this page read as a different product. */}
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary-500">
              {dateline}
            </p>
            <h1 className="mt-2 font-display uppercase leading-none text-ink-900 text-4xl sm:text-5xl break-words">
              {league.name}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
              <StatusChip status={league.league_status} />
              <span className="text-sm text-secondary-600">
                {leagueType === 'season_long' ? 'Season-Long' : 'Weekly Redraft'}
              </span>
            </div>
            {league.description && (
              <p className="mt-2 max-w-2xl text-sm text-secondary-600">
                {league.description}
              </p>
            )}
          </div>

          {isCommissioner && (
            <div className="flex flex-wrap items-start justify-end gap-2 shrink-0">
              {canStartDraft && (
                <div className="text-right">
                  {startDraftError && (
                    <p className="text-sm text-danger-600 mb-1">{startDraftError}</p>
                  )}
                  <button
                    onClick={handleStartDraft}
                    disabled={startDraftLoading}
                    className="btn-primary btn-sm disabled:opacity-50"
                  >
                    {startDraftLoading
                      ? <div className="loading-spinner w-4 h-4 mr-2" />
                      : <PlayIcon className="w-4 h-4 mr-2" />}
                    {startDraftLoading ? 'Starting...' : 'Start Draft'}
                  </button>
                </div>
              )}
              {league?.league_status === 'setup' && (
                <button
                  onClick={handleRegenerateCode}
                  className="btn-outline-secondary btn-sm"
                >
                  Regenerate Invite Code
                </button>
              )}
              <button
                onClick={() => navigate(`/app/leagues/${leagueId}/settings`)}
                aria-label="League settings"
                title="League settings"
                className="icon-btn p-2 border border-secondary-300 text-secondary-600 hover:text-ink-900 hover:border-ink-900 transition-colors"
              >
                <CogIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Standings (active/completed) or Teams membership list (setup/drafting) */}
        <div className="lg:col-span-2">
          <div>
            <div className="flex items-center justify-between gap-3 border-b-2 border-ink-900 pb-1.5 mb-3">
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">
                {showStandings ? 'Standings' : 'Teams'}
              </h3>
              {!showStandings && !userTeam && (teams?.length || 0) < maxTeams && (
                <button onClick={() => setShowJoinModal(true)} className="btn-primary btn-sm">
                  <UserPlusIcon className="w-4 h-4 mr-2" />
                  Join League
                </button>
              )}
            </div>

            {showStandings ? (
              /* ── Standings table ── */
              standingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="loading-spinner w-6 h-6 mr-3" />
                  <span className="text-secondary-600">Loading standings...</span>
                </div>
              ) : standingsError ? (
                <div className="card-body">
                  <p className="text-danger-600 text-sm">Could not load standings.</p>
                </div>
              ) : standings ? (
                <div className="overflow-x-auto">
                  {/* Header */}
                  <div className="grid grid-cols-[1.75rem_1fr_3.25rem_3.25rem] sm:grid-cols-[2rem_1fr_4rem_4rem_3.5rem_5rem] gap-x-3 px-4 py-2 border-b border-secondary-200 text-xs font-medium text-secondary-500 uppercase tracking-wide">
                    <span>#</span>
                    <span>Team</span>
                    <span className="text-right">W-L</span>
                    <span className="text-right">PPG</span>
                    <span className="text-right hidden sm:block">Streak</span>
                    <span className="hidden sm:block">Last 5</span>
                  </div>

                  {standings.teams.map((entry, idx) => {
                    const isMe = entry.user_id === user?.id;
                    const isCutoff = idx === standings.playoff_cutoff;
                    const streakIsWin = entry.streak.startsWith('W');

                    return (
                      <React.Fragment key={entry.team_id}>
                        {/* Playoff cutoff divider */}
                        {isCutoff && (
                          <div className="flex items-center gap-2 px-4 py-1">
                            <div className="flex-1 h-px bg-danger-300" />
                            <span className="text-xs text-danger-500 font-medium whitespace-nowrap">Playoff cutoff</span>
                            <div className="flex-1 h-px bg-danger-300" />
                          </div>
                        )}

                        <div className={`grid grid-cols-[1.75rem_1fr_3.25rem_3.25rem] sm:grid-cols-[2rem_1fr_4rem_4rem_3.5rem_5rem] gap-x-3 items-center px-4 py-3 border-b border-secondary-100 last:border-0 ${isMe ? 'bg-primary-50' : ''}`}>
                          {/* Rank */}
                          <span className={`text-sm font-bold ${idx < standings.playoff_cutoff ? 'text-success-600' : 'text-secondary-400'}`}>
                            {entry.rank}
                          </span>

                          {/* Team name */}
                          <div className="min-w-0">
                            <Link
                              to={`/teams/${entry.team_id}`}
                              className={`text-sm font-medium truncate block hover:underline ${isMe ? 'text-primary-700' : 'text-secondary-900'}`}
                            >
                              {entry.team_name}
                              {isMe && <span className="ml-1 text-xs text-primary-500">(you)</span>}
                            </Link>
                          </div>

                          {/* W-L */}
                          <span className="text-sm text-secondary-700 text-right tabular-nums">
                            {entry.wins}-{entry.losses}
                          </span>

                          {/* PPG */}
                          <span className="text-sm text-secondary-600 text-right tabular-nums">
                            {entry.points_per_game.toFixed(1)}
                          </span>

                          {/* Streak */}
                          <span className={`text-sm font-medium text-right hidden sm:block ${
                            !entry.streak ? 'text-secondary-400' :
                            streakIsWin ? 'text-success-600' : 'text-danger-600'
                          }`}>
                            {entry.streak || '—'}
                          </span>

                          {/* Last 5 pips */}
                          <div className="hidden sm:flex items-center gap-1">
                            {entry.last_5_games.length === 0 ? (
                              <span className="text-xs text-secondary-400">—</span>
                            ) : (
                              entry.last_5_games.map((result, i) => (
                                <span
                                  key={i}
                                  className={`w-4 h-4 rounded-full text-white text-[10px] flex items-center justify-center font-bold ${
                                    result === 'W' ? 'bg-success-500' :
                                    result === 'L' ? 'bg-danger-500' : 'bg-secondary-400'
                                  }`}
                                >
                                  {result}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : null
            ) : (
              /* ── Teams membership list (setup / drafting) ── */
              <div className="card-body">
                {teamsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="loading-spinner w-6 h-6 mr-3" />
                    <span>Loading teams...</span>
                  </div>
                ) : !teams || teams.length === 0 ? (
                  <div className="text-center py-8">
                    <UsersIcon className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
                    <p className="text-secondary-600 mb-4">No teams have joined yet</p>
                    {!userTeam && (
                      <button onClick={() => setShowJoinModal(true)} className="btn-primary">
                        Be the First to Join
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teams.map((team, index) => {
                      const tName = team.team_name || team.teamName || 'Unknown Team';
                      const wins = team.wins || 0;
                      const losses = team.losses || 0;
                      const pointsFor = team.points_for || team.pointsFor || 0;
                      return (
                        <div key={team.id} className="flex items-center justify-between p-3 border border-secondary-200 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-medium">
                              {index + 1}
                            </div>
                            <div>
                              <h4 className="font-medium text-secondary-900">
                                <Link to={`/teams/${team.id}`} className="hover:underline">{tName}</Link>
                              </h4>
                              <p className="text-sm text-secondary-600">
                                {team.user_id === user?.id ? 'You' : 'Team Owner'}
                                {isCommissioner && team.user_id === league.commissioner_id && ' • Commissioner'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-secondary-900">{wins}-{losses}</div>
                            <div className="text-xs text-secondary-600">{Number(pointsFor).toFixed(1)} pts</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* League Actions */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div>
            <h3 className="font-display uppercase text-lg tracking-wide text-ink-900 border-b-2 border-ink-900 pb-1.5 mb-3">Quick Actions</h3>
            <div className="space-y-3">
              {userTeam && (
                <Link to={`/app/teams/${userTeam.id}`} className="btn-primary w-full">
                  <ClipboardDocumentListIcon className="w-4 h-4 mr-2" />
                  Manage My Team
                </Link>
              )}

              {showStandings && (
                <Link
                  to={`/app/leagues/${league.id}/trades`}
                  className="btn-outline-secondary w-full"
                >
                  <ArrowsRightLeftIcon className="w-4 h-4 mr-2" />
                  Trades
                </Link>
              )}

              {showStandings && (
                <Link
                  to={`/app/leagues/${league.id}/waivers`}
                  className="btn-outline-secondary w-full"
                >
                  <InboxStackIcon className="w-4 h-4 mr-2" />
                  Waivers
                </Link>
              )}

              {showStandings && (
                <Link
                  to={`/app/leagues/${league.id}/playoffs`}
                  className="btn-outline-secondary w-full"
                >
                  <TrophyIcon className="w-4 h-4 mr-2" />
                  Playoffs
                </Link>
              )}

              {league?.league_status === 'setup' && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="btn-outline-primary w-full"
                >
                  <UserPlusIcon className="w-4 h-4 mr-2" />
                  Invite Friends
                </button>
              )}
              
              <Link to="/app/players" className="btn-outline-secondary w-full">
                Research Players
              </Link>
            </div>
          </div>

          {/* League Info */}
          <div>
            <h3 className="font-display uppercase text-lg tracking-wide text-ink-900 border-b-2 border-ink-900 pb-1.5 mb-3">League Info</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-secondary-600">Teams:</span>
                <span className="text-secondary-900">
                  {teams?.length || 0} / {maxTeams}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Season:</span>
                <span className="text-secondary-900">{seasonYear}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Draft Date:</span>
                <span className="text-secondary-900">
                  {draftDate ? formatDateTime(draftDate).split(',')[0] : 'TBD'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Entry Fee:</span>
                <span className="text-secondary-900">
                  {entryFee > 0 ? formatCurrency(entryFee) : 'Free'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Created:</span>
                <span className="text-secondary-900">
                  {new Date(createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Commissioner:</span>
                <span className="text-secondary-900">
                  {isCommissioner ? 'You' : 'League Owner'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Visibility:</span>
                <span className="text-secondary-900">{isPublic ? 'Public' : 'Private'}</span>
              </div>
              {leagueType === 'season_long' && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">Roster:</span>
                  <span className="text-secondary-900">2K + 2P</span>
                </div>
              )}
            </div>
          </div>

          {/* Next Steps */}
          {league.league_status === 'setup' && (
            <div>
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900 border-b-2 border-ink-900 pb-1.5 mb-3 flex items-center gap-2">
                <InformationCircleIcon className="w-5 h-5 text-gold-700" />
                Next Steps
              </h3>
              <div>
                {canStartDraft && (
                  <StartLeaguePanel
                    teamCount={teams?.length || 0}
                    maxTeams={league.max_teams ?? null}
                    onStart={handleStartDraft}
                    loading={startDraftLoading}
                    error={startDraftError}
                  />
                )}
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-3 ${(teams?.length || 0) >= 2 ? 'bg-success-500' : 'bg-secondary-300'}`}></div>
                    Get at least 2 teams ({teams?.length || 0} joined)
                  </li>
                  {leagueType === 'season_long' && (
                    <li className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-3 ${draftDate ? 'bg-success-500' : 'bg-secondary-300'}`}></div>
                      Schedule draft date
                    </li>
                  )}
                  <li className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-3 ${canStartDraft ? 'bg-success-500' : 'bg-secondary-300'}`}></div>
                    Start the {leagueType === 'season_long' ? 'draft' : 'season'}
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Matchup widget — any league with a schedule */}
      {shouldShowMatchups(matchups.length) && (
        <MatchupsCard
          matchups={matchups}
          teams={teams ?? null}
          user={user}
          isCommissioner={isCommissioner}
          matchupsLoading={matchupsLoading}
          matchupsError={matchupsError}
          onOpenBoxscore={(id) => navigate(`/app/leagues/${leagueId}/matchups/${id}`)}
          onSimulateWeek={handleSimulateWeek}
          onGenerateSchedule={handleGenerateSchedule}
          simulatingWeek={simulatingWeek}
          simulateError={simulateError}
          scheduleGenerating={scheduleGenerating}
          scheduleError={scheduleError}
          scheduleMessage={scheduleMessage}
        />
      )}

      {/* Points Race */}
      {showStandings && matchups && teams && (() => {
        const race = cumulativeRace(matchups, teams);
        if (race.length === 0 || race.every(s => s.final === 0)) return null;
        const ranked = [...race].sort((a, b) => b.final - a.final);
        const leaderId = ranked[0].teamId;
        const weeks = race[0].points.map(p => p.x);
        // If the user's gold label would overlap the leader's at the right edge
        // (a close race), drop the leader's emphasis — its line stays, gold wins.
        const userSeries = ranked.find(s => s.teamId === userTeam?.id);
        const yMax = Math.ceil(Math.max(...ranked.map(s => s.final)) * 1.1);
        const collide = !!userSeries && userSeries.teamId !== leaderId &&
          Math.abs(ranked[0].final - userSeries.final) / yMax < 0.06; // ~12px at height 240
        return (
          <div>
            <h3 className="font-display uppercase text-lg tracking-wide text-ink-900 border-b-2 border-ink-900 pb-1.5 mb-3">Points Race</h3>
            <div className="">
              <LineChart
                height={240}
                series={ranked.map((s, i) => ({
                  label: s.label,
                  points: s.points,
                  color: s.teamId === userTeam?.id ? '#F4B400' : `rgba(27,26,23,${Math.max(0.25, 0.9 - i * 0.1)})`,
                  emphasis: s.teamId === userTeam?.id || (s.teamId === leaderId && !collide),
                }))}
                xTicks={weeks}
                ariaLabel="Cumulative points by week for every team"
              />
            </div>
          </div>
        );
      })()}

      {/* League Wire */}
      {showStandings && (
        <LeagueWire transactions={transactions} trades={leagueTrades} loading={transactionsLoading} />
      )}

      {/* Join League Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-display uppercase text-2xl leading-none text-ink-900 mb-4">Join League</h3>
            <p className="text-secondary-600 mb-4">
              Enter your team name to join {league.name}:
            </p>
            <input
              type="text"
              placeholder="Enter team name..."
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className="form-input w-full mb-4"
              maxLength={50}
            />
            <div className="flex space-x-3">
              <button 
                onClick={() => {
                  setShowJoinModal(false);
                  setTeamName('');
                }}
                className="btn-outline-secondary flex-1"
                disabled={joinLoading}
              >
                Cancel
              </button>
              <button 
                onClick={handleJoinLeague}
                className="btn-primary flex-1"
                disabled={joinLoading || !teamName.trim()}
              >
                {joinLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="loading-spinner w-4 h-4 mr-2"></div>
                    Joining...
                  </div>
                ) : (
                  'Join League'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="font-display uppercase text-2xl leading-none text-ink-900 mb-4">Invite Friends</h3>
            <p className="text-secondary-600 mb-2">Share your invite code:</p>
            <div className="bg-secondary-100 p-3 rounded-lg text-center mb-2">
              <code className="text-lg font-bold tracking-widest">{league?.invite_code}</code>
            </div>
            <p className="text-secondary-500 text-sm mb-4">Or share the invite link:</p>
            <div className="bg-secondary-100 p-3 rounded-lg text-center mb-4">
              <code className="text-sm break-all">
                {`${window.location.origin}/join/${league?.invite_code}`}
              </code>
            </div>
            <div className="flex space-x-3 mb-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/join/${league?.invite_code}`)
                    .then(() => {
                      setInviteCopied(true);
                      setTimeout(() => setInviteCopied(false), 2000);
                    })
                    .catch(() => {
                      // silently ignore — clipboard not available
                    });
                }}
                className="btn-primary flex-1"
              >
                {inviteCopied ? 'Copied!' : 'Copy Invite Link'}
              </button>
              <button
                onClick={() => setShowInviteModal(false)}
                className="btn-outline-secondary flex-1"
              >
                Close
              </button>
            </div>
            {isCommissioner && league?.league_status === 'setup' && (
              <div className="border-t border-secondary-100 pt-3">
                <button
                  onClick={handleRegenerateCode}
                  disabled={regenerateLoading}
                  className="btn-outline-secondary w-full text-sm disabled:opacity-50"
                >
                  {regenerateLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="loading-spinner w-4 h-4 mr-2" />
                      Regenerating...
                    </div>
                  ) : (
                    'Regenerate Code'
                  )}
                </button>
                <p className="text-xs text-secondary-400 text-center mt-1">
                  This invalidates the current link
                </p>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}